import { Fragment, useMemo } from 'react';
import { BigText, Content, Stack, Strong, Text } from '@kinesis/bungle';
import { CommunitySubSector, DisclosureSectorsPage } from '@/types';
import { renderActivity, renderCommunitySubSector } from '@/data/labels';
import { renderShare, renderUnit, renderValue } from '@/data/units';
import { communitySubSectorColors } from '@/data/colors';
import { ShareBar } from '@/components/share-bar';
import { DisclosureTreemap } from '@/components/disclosure-treemap';
import {
  DisclosureTable,
  Cell,
} from '@/components/disclosure-table/disclosure-table.styles';

// Renders the Community disclosure's "Emissions by sector" page — the
// by-sub-sector counterpart to the Overview's by-source breakdown, on its own
// page: the headline total, the "by sector" table (one row per sub-sector with
// its CO₂-e, share of the grand total as % + a coloured bar, ranked
// largest-first by the server), and the "summary by sector" treemap
// (SubSector → Activity, sized by CO₂-e). The emissions `unit` is server-fixed
// at page scope; `share` is server-computed — the FE only formats and sizes
// the bar/boxes. Colour is the sub-sector palette; treemap leaves are
// auto-shaded tints of their sub-sector. Store-less per the component/page
// split; the page owns the RTK call.
const DisclosureSectors = (props: DisclosureSectorsProps) => {
  const { pageData, onSelectSector, showActivityBreakdown = false } = props;
  const { unit, total, bySector } = pageData;
  // Map the payload into the treemap's shape — label + palette lookup only;
  // the activity totals come straight from the server. Memoised so the
  // treemap's `options` only rebuild when `bySector` actually changes.
  const treemapGroups = useMemo(
    () =>
      bySector.map((sector) => ({
        key: sector.subSector,
        label: renderCommunitySubSector(sector.subSector),
        color: communitySubSectorColors[sector.subSector],
        children: sector.activities.map((activity) => ({
          key: activity.activity,
          label: renderActivity(activity.activity),
          value: activity.total,
        })),
      })),
    [bySector],
  );
  // The treemap reports the clicked GROUP key, which on this page is the
  // sub-sector — narrow it and deep-link to that sub-sector's page.
  const handleSelect = useMemo(
    () =>
      onSelectSector
        ? (key: string) => onSelectSector(key as CommunitySubSector)
        : undefined,
    [onSelectSector],
  );
  return (
    <Stack space='medium'>
      <Stack space='small'>
        <Text tone='secondary'>Total emissions</Text>
        <BigText>{renderValue(total, unit)}</BigText>
        <Text tone='secondary'>{renderUnit(unit)}</Text>
      </Stack>
      {bySector.length > 0 && (
        <Stack space='small'>
          <Strong>Emissions by sector</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTable $columns='minmax(0, 1fr) minmax(96px, 0.5fr) auto auto'>
              <Cell $heading>Sector</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                {renderUnit(unit)}
              </Cell>
              <Cell $heading $align='right'>
                %
              </Cell>
              {bySector.map((sector) => (
                <Fragment key={sector.subSector}>
                  <Cell>{renderCommunitySubSector(sector.subSector)}</Cell>
                  <Cell>
                    <ShareBar
                      share={sector.share}
                      color={communitySubSectorColors[sector.subSector]}
                    />
                  </Cell>
                  <Cell $align='right'>{renderValue(sector.total, unit)}</Cell>
                  <Cell $align='right'>{renderShare(sector.share)}</Cell>
                  {/* One level deeper: the sub-sector's activities, indented
                      under it with the same magnitude line. Rendered in the
                      server's order — the FE adds no sorting. Gated by the
                      `community.disclosure-depth` flag. */}
                  {showActivityBreakdown &&
                    sector.activities.map((activity) => (
                      <Fragment key={activity.activity}>
                        <Cell $indent>{renderActivity(activity.activity)}</Cell>
                        <Cell>
                          <ShareBar
                            share={activity.share}
                            color={communitySubSectorColors[sector.subSector]}
                          />
                        </Cell>
                        <Cell $align='right'>
                          {renderValue(activity.total, unit)}
                        </Cell>
                        <Cell $align='right'>
                          {renderShare(activity.share)}
                        </Cell>
                      </Fragment>
                    ))}
                </Fragment>
              ))}
              <Cell $heading>Total</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                {renderValue(total, unit)}
              </Cell>
              <Cell $heading $align='right'>
                {renderShare(1)}
              </Cell>
            </DisclosureTable>
          </Content>
        </Stack>
      )}
      {bySector.length > 0 && (
        <Stack space='small'>
          <Strong>Summary by sector</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTreemap
              groups={treemapGroups}
              unit={unit}
              onSelect={handleSelect}
            />
          </Content>
        </Stack>
      )}
    </Stack>
  );
};

export type DisclosureSectorsProps = {
  pageData: DisclosureSectorsPage;
  // When provided, clicking a treemap box navigates to that sub-sector's
  // page. The page owns navigation (component/page split); this is store-less.
  onSelectSector?: (subSector: CommunitySubSector) => void;
  // When true (the `community.disclosure-depth` flag), each sub-sector row
  // expands to show its activities one level deeper — indented, same
  // magnitude line. Default false: just the sub-sector rows.
  showActivityBreakdown?: boolean;
};

export { DisclosureSectors };
